<template>
  <div v-if="dataPermission && dataPermission.includes('CNPS_CSS_QR_GET_DETAIL')">
    <div class="breadcrumb-area pos-relative w-100">
      <h1 style="font-size: 18px; font-weight: 500">
        Chi tiết
        <span class="font-weight-bold" style="font-size: 20px">Sticker QR</span>
      </h1>
      <div class="float-right btn-group">
        <button
          type="button"
          class="font-medium f12 btn btn-outline-secondary mr-1"
          @click="$router.push('/customer-support/account-qr')"
        >
          <b-icon icon="arrow-left" aria-hidden="true" />
          Quay lại
        </button>
        <b-link
          v-show="
            info.status === 'PRINTED' &&
            dataPermission &&
            dataPermission.includes('CNPS_CSS_QR_UPLOAD_IMAGE_WEB')
          "
          :to="`/customer-support/account-qr/${id}/edit-sticker`"
          class="font-medium f12 btn btn-outline-primary mr-1"
        >
          <b-icon icon="check-lg" aria-hidden="true" />
          Cập nhật dán Sticker
        </b-link>
        <button
          v-show="dataPermission && dataPermission.includes('CNPS_CSS_QR_UPDATE_QR_NAME')"
          type="button"
          class="font-medium f12 btn btn-outline-primary mr-1"
          @click="showUpdateQrNameModal"
        >
          <b-icon icon="pencil-square" aria-hidden="true" />
          Cập nhật tên QR
        </button>
        <!-- Trong template, thay đổi nút "Cập nhật QR CODE" -->
        <button
          v-show="
            dataPermission &&
            (dataPermission.includes('CNPS_CSS_QR_UPDATE') ||
              dataPermission.includes('CNPS_CSS_QR_UPDATE_QR_NAME')) &&
            info?.integratedMethod === 'VAMC'
          "
          type="button"
          class="font-medium f12 btn btn-outline-danger mr-1"
          @click="showUpdateQrCodeModal"
        >
          <b-icon icon="x-circle" aria-hidden="true" />
          Xóa nội dung mặc định
        </button>
      </div>
      <hr />
    </div>

    <b-row class="gx-4 px-20">
      <!-- Box 1: Thông tin chung -->
      <b-col cols="6" md="6">
        <DetailInfo
          title="Thông tin merchant"
          :info="info"
          :isLoading="isLoading"
          :fields="fieldsMerchantInfo"
        >
          <template #merchantAccountInfo.name="{ value }">
            <b-link
              size="sm"
              :href="`/customer-support/account/${info?.merchantAccountInfo?.id}`"
              target="_blank"
              class="form-control-plaintext f12 text-primary font-weight-bold"
              >{{ value }}</b-link
            >
          </template>
          <template #merchantAccountInfo.email="{ value }">
            <a class="form-control-plaintext f12 text-primary" :href="`mailto:${value}`">
              {{ value }}
            </a>
          </template>
        </DetailInfo>
        <DetailInfo
          title="Thông tin Sticker QR"
          :info="info"
          :isLoading="isLoading"
          :fields="fieldsStickerQr"
        >
          <template #bankCode="{ value }">
            {{ getBankName(value) || value }}
          </template>
          <template #name="{ value }">
            {{ info?.qrDisplayName || value }}
          </template>
          <template #lastModifiedPrinted="{ value }">
            {{ value | formatDate('HH:mm, DD/MM/YYYY') }}
          </template>
          <template #lastModifiedPasted="{ value }">
            {{ value | formatDate('HH:mm, DD/MM/YYYY') }}
          </template>
          <template #lastModifiedStop="{ value }">
            {{ value | formatDate('HH:mm, DD/MM/YYYY') }}
          </template>
          <template #status>
            <b-badge :variant="statusColor">{{ getNameStatus }}</b-badge>
          </template>
        </DetailInfo>
      </b-col>
      <b-col cols="6" class="pl-0" md="6">
        <b-card class="form-detail mb-3 h-mo">
          <h1>Thông tin dán Sticker QR</h1>
          <template v-if="!isLoading">
            <div class="info-content pl-1">
              <div class="image-section">
                <div
                  :class="{ 'h-fit-image': !itemImages.url }"
                  class="image-row"
                  v-for="itemImages in listImages"
                  :key="itemImages.type"
                >
                  <label>{{ itemImages.label }}</label>
                  <div class="image-container">
                    <div v-if="itemImages.url" class="image-layout">
                      <img v-viewer :src="itemImages.url" alt="QR Closeup" />
                      <div class="w-75 ml-3">
                        <p>Thời gian: {{ formatDate(itemImages.updateAt) }}</p>
                        <p>Bởi: {{ itemImages.userName }}</p>
                      </div>
                    </div>
                    <div v-else class="image-placeholder">Chưa có ảnh</div>
                  </div>
                </div>
              </div>
              <div class="mt-3">
                <span style="font-weight: 600; font-size: 13px">Ghi chú:</span>
                <span class="ml-1">{{ info.note || '' }}</span>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="spinner-center">
              <b-spinner class="align-middle" />
            </div>
          </template>
        </b-card>
      </b-col>
    </b-row>
    <b-modal
      id="updateQrNameModal"
      ref="updateQrNameModal"
      title="Cập nhật tên QR"
      @ok="handleUpdateQrName"
      @cancel="resetUpdateQrNameForm"
      :ok-disabled="!newQrName.trim()"
      :ok-title="loadUpdateQrName ? 'Loading...' : 'Cập nhật'"
      cancel-title="Hủy"
    >
      <b-form @submit.prevent="handleUpdateQrName">
        <b-form-group
          id="qr-name-group"
          label="Tên QR mới:"
          label-for="qr-name-input"
          :invalid-feedback="qrNameError"
          :state="qrNameState"
        >
          <b-form-input
            id="qr-name-input"
            v-model="normalizedQrName"
            type="text"
            placeholder="Nhập tên QR mới"
            :state="qrNameState"
            maxlength="50"
            trim
          />
          <small class="form-text text-muted">
            Tên QR hiện tại: {{ info?.qrDisplayName || info?.name || 'Chưa có tên' }}
          </small>
        </b-form-group>
      </b-form>
    </b-modal>
    <!-- Thêm modal confirm mới sau modal updateQrNameModal -->
    <b-modal
      id="updateQrCodeModal"
      ref="updateQrCodeModal"
      title="Xác nhận xóa nội dung mặc định"
      @ok="handleUpdateQrCode"
      :ok-disabled="loadUpdateQrCode"
      :ok-title="loadUpdateQrCode ? 'Loading...' : 'Xác nhận'"
      cancel-title="Hủy"
    >
      <p>Bạn có chắc chắn muốn xóa nội dung mặc định QR không?</p>
      <p class="text-muted">Hành động này không thể hoàn tác.</p>
    </b-modal>
  </div>
</template>
<script>
  import { mapGetters } from 'vuex'
  import { DetailSticker } from '@/services/customer-support/account'
  import { showMess } from '@/config/utils'
  import { CommonService } from '@/services/common.service'
  import moment from 'moment'
  import DetailInfo from '@/components/cskh/DetailInfo'
  import { StaffService } from '@/services/staff.service'

  export default {
    components: {
      DetailInfo,
    },
    data() {
      return {
        moList: [],
        maList: [],
        storeList: [],
        isLoading: false,
        mappingLabel: [],
        mappingStatus: [],
        id: '',
        info: {},
        listBankCode: [],
        fieldsMerchantInfo: [
          { key: 'merchantAccountInfo.name', label: 'Tên merchant' },
          { key: 'merchantAccountInfo.email', label: 'Email' },
          { key: 'mobileUserName', label: 'Địa chỉ' },
        ],
        fieldsStickerQr: [
          { key: 'bankCode', label: 'Ngân hàng' },
          { key: 'name', label: 'Tên tài khoản' },
          { key: 'mobileUserName', label: 'Mobile User' },
          { key: 'bankAccountNumber', label: 'Số tài khoản' },
          { key: 'lastModifiedPrinted', label: 'Ngày in' },
          { key: 'lastModifiedPasted', label: 'Ngày dán' },
          { key: 'lastModifiedStop', label: 'Ngày huỷ' },
          { key: 'status', label: 'Trạng thái' },
        ],
        newQrName: '',
        qrNameError: '',
        isUpdatingQrName: false,
        loadUpdateQrName: false,
        rawQrName: '',
        loadUpdateQrCode: false,
      }
    },

    created() {
      this.id = this.$router.currentRoute.params.id
    },

    async mounted() {
      await this.configSystem()
      if (this.dataPermission.includes('CNPS_CSS_QR_GET_DETAIL')) {
        await this.loadData()
      } else {
        this.$bvModal
          .msgBoxOk('Bạn không có quyền truy cập vào trang này', {
            title: 'Cảnh báo',
            okVariant: 'danger',
            headerClass: 'p-2 border-bottom-0',
            footerClass: 'p-2 border-top-0',
            centered: true,
          })
          .then(() => {
            this.$router.replace('/')
          })
      }
    },

    computed: {
      ...mapGetters({
        dataPermission: 'role/dataPermission',
      }),
      normalizedQrName: {
        get() {
          return this.newQrName // Hiển thị giá trị đã chuẩn hóa
        },
        set(value) {
          this.rawQrName = value
          this.newQrName = this.normalizeText(value)
          // Force update input để hiển thị giá trị đã chuẩn hóa
          this.$nextTick(() => {
            const input = document.getElementById('qr-name-input')
            if (input) {
              input.value = this.newQrName
            }
          })
        },
      },
      qrNameState() {
        if (!this.newQrName.trim()) return null
        if (this.qrNameError) return false
        return true
      },
      listImages() {
        return this.mappingLabel.map(i => {
          const dataImage = this.info?.images?.filter(
            item => Number(item.type) === Number(i.type)
          )[0]
          return {
            ...i,
            label: i.name,
            ...dataImage,
            url: dataImage?.linkUrl,
          }
        })
      },
      getNameStatus() {
        return this.mappingStatus.filter(i => i.code === this.info.status)[0]?.name
      },
      statusColor() {
        switch (this.info.status) {
          case 'PRINTED':
            return 'primary'
          case 'ACTIVE':
            return 'success'
          case 'PASTED_QR':
            return 'secondary'
          case 'NOT_PRINTED':
            return 'warning'
          case 'NOT_PASTED_QR':
            return 'warning'
          default:
            return 'secondary'
        }
      },
      mapBankName() {
        return this.listBankCode.reduce((map, item) => {
          map[item.id] = item.text
          return map
        }, {})
      },
      getBankName() {
        return bank => this.mapBankName[bank] || ''
      },
    },

    methods: {
      normalizeText(text) {
        if (!text) return ''

        // Bảng chuyển đổi ký tự có dấu sang không dấu
        const vietnameseMap = {
          à: 'a',
          á: 'a',
          ả: 'a',
          ã: 'a',
          ạ: 'a',
          ă: 'a',
          ằ: 'a',
          ắ: 'a',
          ẳ: 'a',
          ẵ: 'a',
          ặ: 'a',
          â: 'a',
          ầ: 'a',
          ấ: 'a',
          ẩ: 'a',
          ẫ: 'a',
          ậ: 'a',
          è: 'e',
          é: 'e',
          ẻ: 'e',
          ẽ: 'e',
          ẹ: 'e',
          ê: 'e',
          ề: 'e',
          ế: 'e',
          ể: 'e',
          ễ: 'e',
          ệ: 'e',
          ì: 'i',
          í: 'i',
          ỉ: 'i',
          ĩ: 'i',
          ị: 'i',
          ò: 'o',
          ó: 'o',
          ỏ: 'o',
          õ: 'o',
          ọ: 'o',
          ô: 'o',
          ồ: 'o',
          ố: 'o',
          ổ: 'o',
          ỗ: 'o',
          ộ: 'o',
          ơ: 'o',
          ờ: 'o',
          ớ: 'o',
          ở: 'o',
          ỡ: 'o',
          ợ: 'o',
          ù: 'u',
          ú: 'u',
          ủ: 'u',
          ũ: 'u',
          ụ: 'u',
          ư: 'u',
          ừ: 'u',
          ứ: 'u',
          ử: 'u',
          ữ: 'u',
          ự: 'u',
          ỳ: 'y',
          ý: 'y',
          ỷ: 'y',
          ỹ: 'y',
          ỵ: 'y',
          đ: 'd',
        }

        return text
          .toLowerCase()
          .split('')
          .map(char => vietnameseMap[char] || char)
          .join('')
          .toUpperCase()
      },

      showUpdateQrNameModal() {
        const currentName = this.info?.qrDisplayName || this.info.name || ''
        this.rawQrName = currentName
        this.newQrName = currentName // Hiển thị tên hiện tại
        this.qrNameError = ''
        this.$refs.updateQrNameModal.show()
      },

      resetUpdateQrNameForm() {
        this.rawQrName = ''
        this.newQrName = ''
        this.qrNameError = ''
      },

      showUpdateQrCodeModal() {
        this.$refs.updateQrCodeModal.show()
      },

      async handleUpdateQrCode(bvModalEvt) {
        bvModalEvt.preventDefault()
        this.loadUpdateQrCode = true

        try {
          const response = await StaffService.updateQrName({
            id: this.id,
            typeUpdate: 'REMOVE_DESC_QR_CODE',
          })

          if (response.code === 1000) {
            showMess(
              this,
              'Thành công',
              'Cập nhật QR CODE thành công',
              'success',
              'b-toaster-top-right',
              5000
            )

            this.$refs.updateQrCodeModal.hide()
            this.loadData()
          } else {
            showMess(
              this,
              'Lỗi',
              response?.message || 'Có lỗi xảy ra khi cập nhật QR CODE',
              'danger',
              'b-toaster-top-right',
              5000
            )
          }
        } catch (error) {
          showMess(
            this,
            'Lỗi',
            error.response?.data?.message || error.response?.message || error.message,
            'danger',
            'b-toaster-top-right',
            5000
          )
        } finally {
          this.loadUpdateQrCode = false
        }
      },

      async handleUpdateQrName(bvModalEvt) {
        bvModalEvt.preventDefault()
        this.loadUpdateQrName = true

        if (!this.newQrName.trim()) {
          this.qrNameError = 'Vui lòng nhập tên QR'
          return
        }

        if (this.newQrName.trim() === this.info.qrName) {
          this.qrNameError = 'Tên QR mới phải khác tên hiện tại'
          return
        }

        try {
          this.isUpdatingQrName = true

          // Gọi API cập nhật tên QR
          const response = await StaffService.updateQrName({
            id: this.id,
            qrName: this.newQrName.trim(),
            typeUpdate: 'UPDATE_QR_DISPLAY_NAME',
          })

          if (response.code === 1000) {
            // Cập nhật thông tin local
            this.info.qrName = this.newQrName.trim()

            showMess(
              this,
              'Thành công',
              'Cập nhật tên QR thành công',
              'success',
              'b-toaster-top-right',
              5000
            )

            // Đóng modal
            this.$refs.updateQrNameModal.hide()
            this.resetUpdateQrNameForm()
            this.loadData()
          } else {
            this.qrNameError = response?.message || 'Có lỗi xảy ra khi cập nhật tên QR'
            showMess(
              this,
              'Lỗi',
              response?.message || 'Có lỗi xảy ra khi cập nhật tên QR',
              'danger',
              'b-toaster-top-right',
              5000
            )
          }
        } catch (error) {
          this.qrNameError = error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật tên QR'
          showMess(
            this,
            'Lỗi',
            error.response?.data?.message || error.response?.message || error.message,
            'danger',
            'b-toaster-top-right',
            5000
          )
        } finally {
          this.isUpdatingQrName = false
          this.loadUpdateQrName = false
        }
      },
      async configSystem() {
        try {
          const res = await CommonService.getConfigSystem()
          this.moList = res.data.moContact
          this.maList = res.data.maContact
          this.storeList = res.data.storeContact
          this.mappingLabel = res.data.imageQRType
          this.mappingStatus = res.data.qrStatus

          this.listBankCode = res.data.banks.map(e => {
            return {
              id: e.code,
              text: e.name,
            }
          })
        } catch (e) {
          showMess(
            this,
            'Error',
            e.response?.data?.message || e.response?.message || e.message,
            'danger',
            'b-toaster-top-right',
            5 * 60 * 60
          )
        }
      },
      formatDate(date, format = 'HH:mm, DD/MM/YYYY') {
        if (!date) return
        return moment(date).format(format)
      },
      async loadData() {
        try {
          this.isLoading = true
          const res = await DetailSticker(this.id)
          this.info = res.data
        } catch (e) {
          showMess(
            this,
            'Lỗi',
            e.response?.data?.message || e.response?.message || e.message,
            'danger',
            'b-toaster-top-right',
            5 * 60 * 60
          )
        } finally {
          this.isLoading = false
        }
      },
    },
  }
</script>
<style lang="scss" scoped>
  .breadcrumb {
    margin-bottom: 0;
  }

  .h-fit {
    height: fit-content !important;
  }

  .image-row {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .image-row label {
      color: black;
      font-weight: 600;
    }
  }

  .h-fit-image {
    display: flex !important;
    flex-direction: column !important;
    gap: 0 !important;

    .image-container {
      height: 35px !important;
    }
  }

  label {
    color: black;
    font-weight: 600;
  }

  .image-container {
    width: 100%;
    height: 80px;
    border-radius: 4px;

    .image-layout {
      display: flex;
      gap: 10px;
      height: 70px;

      img {
        max-width: 100%;
        max-height: 100%;
        width: 70px;
        object-fit: cover;
        background: white;
        border-radius: 5px;
        border: 1px solid gray;
      }
    }

    .image-placeholder {
      color: #999;
      font-size: 13px;
      font-weight: 300;
    }
  }

  .shadow-card {
    // box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    border: 0.1px solid #e4e4e4;
  }

  .min-h-290 {
    min-height: 290px;
  }

  .h-mo {
    min-height: 430px;
  }

  .h-mo.b-table-sticky-header {
    max-height: 36.5svh !important;
  }

  .card-footer {
    background-color: #fff;
    border-top: none !important;
  }

  .b-table-sticky-header {
    min-height: 29.5svh;
    max-height: 29.5svh;
  }

  .card-title {
    font-size: 16px !important;
    font-weight: bold;
    padding-bottom: 10px;
  }

  .b-table-sticky-header {
    border-radius: 0 !important;
  }

  .form-control-plaintext,
  .col-form-label {
    padding: 0.175rem !important;
  }

  .spinner-center {
    display: flex;
    justify-content: center;
  }

  .form-group {
    margin-bottom: 0 !important;
  }

  .table td {
    background: white !important;
  }

  .b-badge {
    padding: 10px;
    font-size: 14px;
    border-radius: 4px;
  }
  .form-detail {
    box-shadow: none !important;
    h1 {
      border-bottom: 1px solid #e9e9e9;
    }
  }
</style>
